import * as React from 'react'

// Type for auth service
interface AuthService {
  getCurrentUser(): Promise<{ user: any; error: any }>
  getUserProfile(): Promise<{ data: any; error: any }>
}

// Import auth service (will be dynamically imported to avoid build issues in Storybook)
let authService: AuthService | null = null
const getAuthService = async (): Promise<AuthService> => {
  if (!authService) {
    try {
      const module = await import('@joinomu/shared')
      authService = module.authService
    } catch (error) {
      console.warn('Auth service not available (likely in Storybook):', error)
      // Return mock service for Storybook
      authService = {
        getCurrentUser: async () => ({ user: null, error: null }),
        getUserProfile: async () => ({ data: null, error: null })
      }
    }
  }
  return authService
}

export interface PhoneNumberCheckResult {
  /** Whether the user has a phone number */
  hasPhoneNumber: boolean
  /** Whether the check is still loading */
  loading: boolean
  /** Any error that occurred */
  error: string | null
  /** Function to manually recheck phone number */
  recheckPhoneNumber: () => void
}

/**
 * Hook to check if the current user has a phone number in their profile
 * Returns loading state, whether they have a phone number, and a recheck function
 */
export function usePhoneNumberCheck(): PhoneNumberCheckResult {
  const [hasPhoneNumber, setHasPhoneNumber] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const checkPhoneNumber = React.useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const auth = await getAuthService()
      
      // Get current user first
      const { user, error: userError } = await auth.getCurrentUser()
      if (userError || !user) {
        console.log('No user found for phone number check')
        setHasPhoneNumber(false)
        setLoading(false)
        return
      }

      // Get user profile to check phone number
      const { data: profile, error: profileError } = await auth.getUserProfile()
      if (profileError) {
        console.error('Error fetching user profile for phone check:', profileError)
        setError('Failed to check phone number status')
        setHasPhoneNumber(false)
        setLoading(false)
        return
      }

      // Check if phone number exists and is not empty
      const phone = profile?.phone
      const hasPhone = Boolean(phone && phone.trim().length > 0)
      
      console.log('🔍 Phone number check:', { phone, hasPhone })
      setHasPhoneNumber(hasPhone)
      
    } catch (err) {
      console.error('Exception in phone number check:', err)
      setError('Failed to check phone number')
      setHasPhoneNumber(false)
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial check on mount
  React.useEffect(() => {
    checkPhoneNumber()
  }, [checkPhoneNumber])

  // Listen for phone number update events
  React.useEffect(() => {
    const handlePhoneNumberUpdate = () => {
      console.log('🔄 Phone number update event received - rechecking phone number')
      checkPhoneNumber()
    }

    window.addEventListener('phoneNumberUpdate', handlePhoneNumberUpdate)
    return () => {
      window.removeEventListener('phoneNumberUpdate', handlePhoneNumberUpdate)
    }
  }, [checkPhoneNumber])

  return {
    hasPhoneNumber,
    loading,
    error,
    recheckPhoneNumber: checkPhoneNumber
  }
}